import { useEffect, useState } from "react";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import { t } from "../i18n";

import "./StickyNoteShadowControls.scss";

import type { AppClassProperties } from "../types";

interface BoxShadow {
  color: string;
  opacity: number;
  blur: number;
  offsetX: number;
  offsetY: number;
  enabled: boolean;
}

export const DEFAULT_STICKY_SHADOW: BoxShadow = {
  color: "#0f172a",
  opacity: 0.28,
  blur: 16,
  offsetX: 0,
  offsetY: 8,
  enabled: true,
};

const readShadow = (raw: unknown): BoxShadow => ({
  ...DEFAULT_STICKY_SHADOW,
  ...(typeof raw === "object" && raw !== null ? (raw as object) : {}),
});

/**
 * Shadow controls for sticky notes, rendered inside the native left-side
 * properties panel (SelectedShapeActions). Edits the element's
 * `customData.boxShadow`, which the renderer turns into a real drop shadow.
 */
export const StickyNoteShadowControls = ({
  element,
  app,
}: {
  element: ExcalidrawElement;
  app: AppClassProperties;
}) => {
  // Local state so the controls reflect the user's action immediately, even
  // when the parent panel doesn't re-render (it can be memoized on the same
  // mutated-in-place element reference, which caused rapid toggles to appear
  // to do nothing). We re-sync whenever the selected element or its version
  // changes (selection switch, undo/redo, external edits).
  const [shadow, setShadow] = useState<BoxShadow>(() =>
    readShadow(element.customData?.boxShadow),
  );

  useEffect(() => {
    setShadow(readShadow(element.customData?.boxShadow));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element.id, element.version]);

  const patch = (partial: Partial<BoxShadow>) => {
    const next = { ...shadow, ...partial };
    setShadow(next); // optimistic, immediate UI update
    app.scene.mutateElement(element, {
      customData: { ...element.customData, boxShadow: next },
    });
  };

  return (
    <fieldset className="sticky-shadow-controls">
      <legend>{t("stickyShadow.title")}</legend>

      <label className="sticky-shadow-controls__toggle">
        <input
          type="checkbox"
          checked={shadow.enabled}
          onChange={(e) => patch({ enabled: e.target.checked })}
        />
        <span>{t("stickyShadow.enabled")}</span>
      </label>

      {shadow.enabled && (
        <>
          <div className="sticky-shadow-controls__row">
            <span className="sticky-shadow-controls__label">
              {t("stickyShadow.color")}
            </span>
            <input
              type="color"
              className="sticky-shadow-controls__color"
              value={shadow.color}
              onChange={(e) => patch({ color: e.target.value })}
            />
          </div>

          <div className="sticky-shadow-controls__row">
            <span className="sticky-shadow-controls__label">
              {t("stickyShadow.opacity")}
            </span>
            <input
              type="range"
              min={0}
              max={100}
              value={Math.round(shadow.opacity * 100)}
              onChange={(e) => patch({ opacity: Number(e.target.value) / 100 })}
            />
            <span className="sticky-shadow-controls__val">
              {Math.round(shadow.opacity * 100)}%
            </span>
          </div>

          <div className="sticky-shadow-controls__row">
            <span className="sticky-shadow-controls__label">
              {t("stickyShadow.blur")}
            </span>
            <input
              type="range"
              min={0}
              max={50}
              value={shadow.blur}
              onChange={(e) => patch({ blur: Number(e.target.value) })}
            />
            <span className="sticky-shadow-controls__val">{shadow.blur}</span>
          </div>

          <div className="sticky-shadow-controls__row">
            <span className="sticky-shadow-controls__label">
              {t("stickyShadow.offsetX")}
            </span>
            <input
              type="range"
              min={-40}
              max={40}
              value={shadow.offsetX}
              onChange={(e) => patch({ offsetX: Number(e.target.value) })}
            />
            <span className="sticky-shadow-controls__val">
              {shadow.offsetX}
            </span>
          </div>

          <div className="sticky-shadow-controls__row">
            <span className="sticky-shadow-controls__label">
              {t("stickyShadow.offsetY")}
            </span>
            <input
              type="range"
              min={-40}
              max={40}
              value={shadow.offsetY}
              onChange={(e) => patch({ offsetY: Number(e.target.value) })}
            />
            <span className="sticky-shadow-controls__val">
              {shadow.offsetY}
            </span>
          </div>
        </>
      )}
    </fieldset>
  );
};
